import type {ComputedRef, PropType, Ref} from "vue";

export type InputSize = 'base' | 'xs' | 'xxs';
export type InputState = 'default' | 'error' | 'success' | 'warning' | 'saved' | 'readonly' | 'issues';
export type StatePropType = InputState | Record<string, boolean>;
export type InputStyle = 'default' | 'accounting';
export type LabelStyle = 'floating' | 'static';
export interface InputLoadingState {
  [key: string]: boolean;
}

export interface ExposedInputMethods {
    focus: () => void;
    blur: () => void;
    $el: ComputedRef<HTMLInputElement | null>;
    isFocused: Ref<boolean>;
}

export interface ExposedSelectMethods extends ExposedInputMethods {
    show: () => void;
    hide: () => void;
    clearValue: (hidePopup?: boolean) => void;
}

export const inputProps = {
  modelValue: {
    type: null as unknown as PropType<string | null | undefined | number | boolean>,
    default: '',
    required: false,
  },
  prop: {
    type: String,
    default: '',
    required: false,
  },
  state: {
    type: [String, Object] as PropType<StatePropType>,
    default: 'default',
    required: false,
  },
  label: {
    type: String,
    default: '',
    required: false,
  },
  placeholder: {
    type: String,
    default: null,
    required: false,
  },
  loading: {
    type: Boolean,
    default: false,
    required: false,
  },
  readonly: {
    type: Boolean,
    default: false,
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
    required: false,
  },
  inputStyle: {
    type: String as PropType<InputStyle>,
    default: 'default',
    required: false,
  },
  labelStyle: {
    type: String as PropType<LabelStyle>,
    default: 'floating',
    required: false,
  },
  stateIconPlacement: {
    type: String as PropType<'left' | 'right'>,
    default: 'right',
    required: false,
  },
  bgColor: {
    type: String,
    default: '',
    required: false,
  },
  autofocus: {
    type: Boolean,
    default: false,
    required: false
  }
};

export function useInputState(props: { state: StatePropType, inputStyle: InputStyle, readonly: boolean}) {
  const activeState = computed<InputState>((): InputState => {
    if (typeof props.state === 'string') {
      return props.state;
    }

    if (typeof props.state === 'object' && props.state !== null) {
      for (const [key, value] of Object.entries(props.state)) {
        if (value) {
          return key as InputState;
        }
      }

      return 'default';
    }

    return 'default'; // Fallback in case of unexpected input
  });

  const inputStateClasses = computed(() => {
    if (props.readonly) return 'border-border-primary pointer-events-none';

    return {
      'border-border-primary': activeState.value === 'default' || activeState.value === 'saved',
      '!border-red-900 !placeholder-red-900': activeState.value === 'error' || activeState.value === 'issues',
      '!border-yellow-950 !placeholder-yellow-950 !text-yellow-950': activeState.value === 'warning',
      '!text-red-900': activeState.value === 'issues',
      '!border-green-900': activeState.value === 'success',
    }
  });

  const labelStateClasses = computed(() => {
    if (props.readonly) return 'pointer-events-none text-text-secondary';

    return {
      'text-text-secondary': activeState.value === 'default' || activeState.value === 'saved',
      '!text-red-900': activeState.value === 'error' || activeState.value === 'issues',
      '!text-yellow-950': activeState.value === 'warning',
      '!text-green-900': activeState.value === 'success',
    }
  });

  const prefixSuffixColorClasses = computed(() => {
    return {
      '!text-red-900': activeState.value === 'error' || activeState.value === 'issues',
      '!text-yellow-950': activeState.value === 'warning',
      '!text-green-900': activeState.value === 'success',
    }
  });

  const inputStyleSizeClasses = computed(() => {
    switch (props.inputStyle) {
      case 'accounting':
        return 'px-4 py-2'
      default:
        return 'px-2.5 pb-2.5 pt-3'
    }
  });

  const inputStyleClasses = computed(() => {
    switch (props.inputStyle) {
      case 'accounting':
        return 'rounded-lg'
      default:
        return 'rounded-md'
    }
  })

  return {
    activeState,
    inputStateClasses,
    prefixSuffixColorClasses,
    labelStateClasses,
    inputStyleSizeClasses,
    inputStyleClasses,
  };
}

export function focusNextInputElement(event: KeyboardEvent) {
  const currentElement = event.target as HTMLElement;
  const focusableSelectors = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([readonly])',
    'select:not([disabled])',
    'textarea:not([disabled]):not([readonly])',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ];
  const focusableElements = Array.from(
    document.querySelectorAll<HTMLElement>(focusableSelectors.join(','))
  );

  // Find the index of the current element
  const currentIndex = focusableElements.indexOf(currentElement);
  if (currentIndex === -1) return;

  // Focus the next element if it exists
  const nextElement = focusableElements[currentIndex + 1];
  if (nextElement) {
    currentElement.blur();
    nextElement.focus();
  }
}